import {Optional} from 'util/Types';

// Should match antibody_analysis.py:NUMBERING_SCHEMES
export enum NumberingScheme {
  KABAT = 'kabat',
  CHOTHIA = 'chothia',
  IMGT = 'imgt',
}

// Should match antibody_analysis.py:CDR_DEFINITIONS
export enum CDRDefinition {
  KABAT = 'kabat',
  CHOTHIA = 'chothia',
  IMGT = 'imgt',
  NORTH = 'north',
}

// Organisms supported by ANARCI, not to be confused with the organisms we support in Codon Optimization
export enum Organism {
  HUMAN = 'human',
  MOUSE = 'mouse',
  RAT = 'rat',
  RABBIT = 'rabbit',
  RHESUS = 'rhesus',
  PIG = 'pig',
  ALPACA = 'alpaca',
}

// Chain types ANARCI can detect
export enum ChainType {
  HEAVY = 'H',
  KAPPA = 'K',
  LAMBDA = 'L',
  ALPHA = 'A',
  BETA = 'B',
  DELTA = 'D',
  GAMMA = 'G',
}

export interface Numbering {
  indices: Array<string>;
  start: number;
}

export interface NumberingResponse {
  residues: string;
  scheme: NumberingScheme;
  results: Array<ValidNumberingResult | InvalidNumberingResult>;
}

export interface InvalidNumberingResult {
  message: string;
  organism: Optional<Organism>;
  chainType: Optional<ChainType>;
}

export interface ValidNumberingResult extends Numbering {
  organism: Organism;
  chainType: ChainType;
  message: undefined;
}

export interface CDRAnnotation {
  name: string;
  start: number;
  end: number;
}

export interface CDRAnnotations {
  regions: Array<CDRAnnotation>;
}

export interface CDRResponse {
  residues: string;
  definition: CDRDefinition;
  results: Array<ValidCDRResult | InvalidCDRResult>;
}

export interface InvalidCDRResult {
  message: string;
  organism: Optional<Organism>;
  chainType: Optional<ChainType>;
}

export interface ValidCDRResult extends CDRAnnotations {
  organism: Organism;
  chainType: ChainType;
  message: undefined;
}

export const CHAIN_DISPLAY_NAMES: {[chainType in ChainType]: string} = {
  H: 'Heavy',
  K: 'Kappa',
  L: 'Lambda',
  A: 'TCR Alpha',
  B: 'TCR Beta',
  G: 'TCR Gamma',
  D: 'TCR Delta',
};

export enum LiabilitySiteType {
  NLinkedGlycosylation = 'NLinkedGlycosylation',
  AsparagineDeamidation = 'AsparagineDeamidation',
  AsparagineDeamidationInCDRs = 'AsparagineDeamidationInCDRs',
  TryptophanOxidation = 'TryptophanOxidation',
  MethionineOxidation = 'MethionineOxidation',
  AspartateIsomerization = 'AspartateIsomerization',
}

export interface PTMSite {
  name: LiabilitySiteType;
  matchPos: number;
  viewPos: number;
}

export const LIABILITY_SITE_DISPLAY_NAMES = {
  NLinkedGlycosylation: 'N-Linked Glycosylation',
  AsparagineDeamidation: 'Asparagine Deamidation',
  AsparagineDeamidationInCDRs: 'Asparagine Deamidation (in CDRs)',
  TryptophanOxidation: 'Tryptophan Oxidation',
  MethionineOxidation: 'Methionine Oxidation',
  AspartateIsomerization: 'Aspartate Isomerization',
};
